import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Star, Eye, EyeOff, Trash2, MessageSquareQuote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminTestimonials() {
  const { getAuthHeaders } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/testimonials`, { headers: getAuthHeaders() });
      setItems(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const toggleVisibility = async (item) => {
    try {
      await axios.put(`${API_URL}/admin/testimonials/${item.id}/visibility`,
        { is_approved: !item.is_approved },
        { headers: getAuthHeaders() });
      setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, is_approved: !i.is_approved } : i));
      toast.success(!item.is_approved ? 'Review is now visible' : 'Review hidden');
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const remove = async (item) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try {
      await axios.delete(`${API_URL}/admin/testimonials/${item.id}`, { headers: getAuthHeaders() });
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success('Review deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const filtered = items.filter((i) =>
    filter === 'all' ? true : filter === 'visible' ? i.is_approved : !i.is_approved
  );
  const pendingCount = items.filter((i) => !i.is_approved).length;

  return (
    <div className="p-6" data-testid="admin-testimonials">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            Reviews
          </h1>
          <p className="text-muted-foreground mt-1">
            {items.length} total · <span className="text-[#EA580C]">{pendingCount} awaiting approval</span>
          </p>
        </div>
        <div className="flex gap-2">
          {['all', 'pending', 'visible'].map((f) => (
            <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm"
              className={filter === f ? 'bg-[#0B1120] text-white capitalize' : 'capitalize'}
              onClick={() => setFilter(f)} data-testid={`filter-${f}`}>
              {f}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <MessageSquareQuote className="h-12 w-12 mx-auto mb-4 text-muted-foreground/40" />
          No reviews in this view.
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((item) => (
            <div key={item.id} className="bg-white rounded-xl border border-border p-5" data-testid={`admin-review-${item.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} className={`h-4 w-4 ${s <= item.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                      ))}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${item.is_approved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {item.is_approved ? 'Visible' : 'Pending'}
                    </span>
                  </div>
                  <p className="text-gray-700 mb-2">"{item.message}"</p>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">{item.customer_name}</span>
                    {item.location ? ` · ${item.location}` : ''}
                    {item.created_at ? ` · ${new Date(item.created_at).toLocaleDateString('en-NZ')}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    {item.is_approved ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
                    <Switch checked={item.is_approved} onCheckedChange={() => toggleVisibility(item)} data-testid={`toggle-${item.id}`} />
                  </div>
                  <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600" onClick={() => remove(item)} data-testid={`delete-review-${item.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
